'use client';

import { Star } from 'lucide-react';

import { cn } from '../utils/cn';
import { Checkbox } from './checkbox';
import { Input } from './input';
import { Label } from './label';
import { RadioGroup, RadioGroupItem } from './radio-group';
import { Scale0To10Field } from './scale-0-10-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import { Textarea } from './textarea';

/** Guest form question types — matches backend / public API string enums. */
export type GuestFeedbackQuestionType =
  | 'short_text'
  | 'long_text'
  | 'yes_no'
  | 'nps_0_10'
  | 'rating_1_5'
  | 'single_select'
  | 'multi_select';

export type GuestFeedbackQuestion = {
  id: string;
  /** String union or generated enum value (e.g. api-client `FeedbackQuestionType`). */
  type: GuestFeedbackQuestionType | (string & {});
  label: string;
  required: boolean;
  options?: string[];
};

export type GuestFeedbackAnswerValue = string | number | boolean | string[];

export type GuestFeedbackQuestionFieldLabels = {
  required: string;
  yes: string;
  no: string;
  scaleLow: string;
  scaleHigh: string;
  selectPlaceholder: string;
};

export type GuestFeedbackQuestionFieldsProps = {
  questions: GuestFeedbackQuestion[];
  labels: GuestFeedbackQuestionFieldLabels;
  values?: Record<string, GuestFeedbackAnswerValue | undefined>;
  onChange?: (questionId: string, value: GuestFeedbackAnswerValue) => void;
  /** Per-question validation messages keyed by question id. */
  errors?: Record<string, string | undefined>;
  disabled?: boolean;
  /**
   * Disabled in-app preview: shows sample control states (e.g. scale at 7)
   * instead of empty interactive defaults.
   */
  preview?: boolean;
  className?: string;
};

/**
 * Shared guest form question controls for feedback-collector and in-app preview.
 * Keep both surfaces on this component so 0–10 / NPS and other types never diverge.
 */
export function GuestFeedbackQuestionFields({
  questions,
  labels,
  values = {},
  onChange,
  errors = {},
  disabled = false,
  preview = false,
  className,
}: GuestFeedbackQuestionFieldsProps) {
  if (questions.length === 0) return null;

  return (
    <div className={cn('space-y-7 lg:space-y-8', className)}>
      {questions.map((question) => {
        const error = errors[question.id];
        const errorId = error ? `${question.id}-error` : undefined;
        return (
          <div
            key={question.id}
            className={cn(
              'min-w-0 space-y-2.5',
              // Viewport `lg:` must not run inside phone mocks — preview stays stacked.
              !preview &&
                (question.type === 'yes_no' ||
                  question.type === 'rating_1_5' ||
                  question.type === 'short_text') &&
                'lg:grid lg:grid-cols-[minmax(0,12rem)_minmax(0,1fr)] lg:items-start lg:gap-6 lg:space-y-0',
            )}
          >
            <Label
              className={cn(
                'text-sm font-medium break-words text-foreground',
                !preview && 'lg:pt-2.5',
                error && 'text-destructive',
              )}
            >
              {question.label}
              {question.required ? (
                <span
                  className={cn(
                    'ml-1',
                    error ? 'text-destructive/80' : 'text-muted-foreground',
                  )}
                >
                  ({labels.required})
                </span>
              ) : null}
            </Label>
            <div className="min-w-0 space-y-1.5">
              <GuestFeedbackQuestionControl
                question={question}
                value={values[question.id]}
                onChange={(value) => onChange?.(question.id, value)}
                labels={labels}
                disabled={disabled}
                preview={preview}
                invalid={Boolean(error)}
                describedBy={errorId}
              />
              {error ? (
                <p
                  id={errorId}
                  className="text-xs text-destructive"
                  role="alert"
                >
                  {error}
                </p>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GuestFeedbackQuestionControl({
  question,
  value,
  onChange,
  labels,
  disabled,
  preview,
  invalid,
  describedBy,
}: {
  question: GuestFeedbackQuestion;
  value: GuestFeedbackAnswerValue | undefined;
  onChange: (value: GuestFeedbackAnswerValue) => void;
  labels: GuestFeedbackQuestionFieldLabels;
  disabled: boolean;
  preview: boolean;
  invalid: boolean;
  describedBy?: string;
}) {
  switch (question.type) {
    case 'short_text':
      return (
        <Input
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className="h-11 text-base"
        />
      );
    case 'long_text':
      return (
        <Textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          rows={4}
          className="min-h-28 resize-none text-base"
        />
      );
    case 'yes_no': {
      const current =
        value === true ? 'yes' : value === false ? 'no' : undefined;
      return (
        <RadioGroup
          value={current}
          onValueChange={(v) => onChange(v === 'yes')}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className="grid grid-cols-2 gap-2"
        >
          {(
            [
              { id: 'yes', label: labels.yes },
              { id: 'no', label: labels.no },
            ] as const
          ).map((option) => {
            const selected = current === option.id;
            return (
              <label
                key={option.id}
                htmlFor={`${question.id}-${option.id}`}
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-3 text-sm font-medium transition-colors',
                  selected
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-secondary/40 text-foreground hover:bg-muted/70',
                  invalid && !selected && 'border-destructive/60',
                  disabled && 'pointer-events-none opacity-50',
                )}
              >
                <RadioGroupItem
                  value={option.id}
                  id={`${question.id}-${option.id}`}
                  className="sr-only"
                />
                {option.label}
              </label>
            );
          })}
        </RadioGroup>
      );
    }
    case 'nps_0_10':
      return (
        <Scale0To10Field
          id={question.id}
          value={typeof value === 'number' ? value : undefined}
          onValueChange={onChange}
          disabled={disabled}
          preview={preview}
          lowLabel={labels.scaleLow}
          highLabel={labels.scaleHigh}
          aria-label={question.label}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
        />
      );
    case 'rating_1_5':
      return (
        <div
          className="flex min-w-0 items-center justify-between gap-0.5 sm:justify-start sm:gap-2"
          role="radiogroup"
          aria-label={question.label}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
        >
          {[1, 2, 3, 4, 5].map((n) => {
            const selected =
              preview || (typeof value === 'number' && n <= value);
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={typeof value === 'number' && value === n}
                disabled={disabled}
                onClick={() => onChange(n)}
                aria-label={`${n}`}
                className={cn(
                  'shrink rounded-lg p-1.5 transition-[color,transform,background-color] sm:p-2',
                  'focus-visible:ring-2 focus-visible:ring-foreground focus-visible:outline-none',
                  'motion-safe:active:scale-95',
                  selected
                    ? 'collector-star-selected bg-[color-mix(in_oklab,var(--collector-accent,var(--foreground))_12%,transparent)]'
                    : 'text-muted-foreground/35 hover:bg-muted/60 hover:text-muted-foreground',
                  invalid && !selected && 'text-destructive/40',
                )}
              >
                <Star
                  className="size-6 sm:size-7"
                  fill={selected ? 'currentColor' : 'none'}
                  strokeWidth={1.5}
                />
              </button>
            );
          })}
        </div>
      );
    case 'single_select':
      return (
        <Select
          value={typeof value === 'string' ? value : undefined}
          onValueChange={(v) => onChange(v)}
          disabled={disabled}
        >
          <SelectTrigger
            className="h-11 text-base"
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
          >
            <SelectValue placeholder={labels.selectPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {(question.options ?? []).map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'multi_select': {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div
          className="space-y-2"
          role="group"
          aria-label={question.label}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
        >
          {(question.options ?? []).map((option) => {
            const checked = selected.includes(option);
            return (
              <label
                key={option}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-md border px-3 py-3 text-sm text-foreground transition-colors',
                  checked
                    ? 'border-foreground/30 bg-muted/50'
                    : 'border-border bg-card hover:bg-muted/40',
                  invalid && !checked && 'border-destructive/60',
                  disabled && 'pointer-events-none opacity-50',
                )}
              >
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={(next) => {
                    if (next === true) {
                      onChange([...selected, option]);
                    } else {
                      onChange(selected.filter((v) => v !== option));
                    }
                  }}
                />
                {option}
              </label>
            );
          })}
        </div>
      );
    }
    default:
      return null;
  }
}
